<template>
    <div class="flex flex-col items-center">
        <loading v-if="loading"></loading>
        <div
            v-if="noCalendarIntegration"
            class="text-body-sm mx-16 p-24 flex flex-col justify-center rounded text-center
                    text-primary border-2 w-360 hover:border-primary-60 cursor-pointer"
            :class="`${form.mode === 'calendar' ? 'border-primary hover:border-primary' : 'border-primary-20'}`"
        >
                <span class="block font-semibold uppercase mt-12 mb-8">
                    {{ $t('calendar.no_calendar.info') }}
                </span>
            <no-calendar-integration></no-calendar-integration>
        </div>
        <div v-else class="w-full space-y-32">
            <div v-if="!disabled && !form.instant" class="space-y-32">
                <div v-if="!hasBulkTimeslots">
                    <div
                        class="border rounded-4 p-12"
                        :class="[form.slot_confirmed ? 'border-secondary' : 'border-primary-20', form.calendar_open ? 'opacity-50' : '']"
                    >
                        <checkbox
                            v-model="form.slot_confirmed"
                            :disabled="!!form.calendar_open"
                            class="mr-8 whitespace-nowrap disabled:text-primary-20"
                        >
                            <span class="font-normal">{{ $t('interview.application_confirmed_slot.checkbox') }}</span>
                        </checkbox>
                    </div>
                </div>
                <div>
                    <div class="border rounded-4 p-12"
                         :class="[form.calendar_open ? 'border-secondary' : 'border-primary-20', form.slot_confirmed ? 'opacity-50' : '']"
                    >
                        <checkbox
                            v-model="form.calendar_open"
                            :disabled="form.slot_confirmed"
                            class="mr-8 whitespace-nowrap"
                        >
                            <span class="font-normal">{{ $t('interview.calendar.open.checkbox') }}</span>
                        </checkbox>
                    </div>
                </div>
            </div>
            <div class="flex justify-between gap-32">
                <!-- TODO: Temporarily commented out until buffer implementation is going to be added-->

                <!--                <simple-input
                                    v-model="buffer.before"
                                    type="number"
                                    :min="0"
                                    :step="5"
                                    class="grow"
                                >
                                    Buffer voor (minutes)
                                </simple-input>-->
                <simple-input
                    v-model="form.duration"
                    type="number"
                    :min="5"
                    :step="5"
                    class="grow"
                    @input-blur="updateDuration"
                >{{ $t('general.duration') }}
                </simple-input>
                <!-- TODO: Temporarily commented out until buffer implementation is going to be added-->

              <!--                <simple-input
                                  v-model="buffer.after"
                                  type="number"
                                  :min="0"
                                  :step="5"
                                  class="grow"
                              >
                                  Buffer na (minutes)
                              </simple-input>-->
            </div>
            <div>
                <span class="mb-8 text-body-sm flex items-center first-letter:uppercase font-semibold text-black">
                    {{ $t('general.period') }}
                </span>
                <div class="flex justify-between">
                    <date-picker
                        v-model="period.start"
                        class="grow"
                        :future-limit="futureLimit"
                        :disable-past="true"
                    />
                    <div class="flex items-center justify-center font-semibold text-primary-20 mx-12">
                        {{ $t('interview.until') }}
                    </div>
                    <date-picker
                        v-model="period.end"
                        class="grow"
                        :future-limit="futureLimit"
                        :disable-past="true"
                    ></date-picker>
                </div>
            </div>

            <loading v-if="!renderCalendar && !noCronofySubs"></loading>
            <calendar
                ref="calendar"
                v-else-if="renderCalendar && !noCronofySubs"
                :form="form"
                :available-periods="availablePeriods"
                :creating="creating"
            ></calendar>
            <div v-else class="py-48 w-full bg-primary-20 rounded-sm text-center font-semibold">
                <span v-if="!subs.length && noCronofySubs">{{ $t('calendar.availability.unavailable.no_subs') }}</span>
                <span v-if="subs.length && noCronofySubs">{{ $t('calendar.availability.unavailable.no_cronofy_subs') }}</span>
            </div>
        </div>

        <div class="flex justify-end items-center w-full mt-32">
            <div v-if="hasDatesInThePast" class="mr-16" @click="removeTimeslotsInPast">
                <p class="cursor-pointer underline">{{ $t('interview.timeslots.delete_in_past') }}</p>
            </div>
            <div v-if="!disabled && !creating" class="">
                <simple-button
                    v-if="canManage"
                    display="primary"
                    type="button"
                    :capitalize="true"
                    :uppercase="false"
                    @click="saveTimeslots"
                >
                    {{ $t('general.confirm') }}
                </simple-button>
            </div>
        </div>

        <popup-modal-confirm v-if="showDatesInPastWarning"
                             :message="$t('interviews.timeslots.in_past.description')"
                             confirm-button="success"
                             @close="closeWarning"
                             @confirm="closeWarning"
        >
            <p class="mt-16 font-semibold">{{ $t('interviews.timeslots.in_past.action') }}</p>
        </popup-modal-confirm>
    </div>
</template>

<script>
import CalendarSyncEdit from "../ui/CalendarSyncEdit.vue";
import Form from "../../etc/forms/Form";
import * as calendar from "../../services/CalendarService";
import {mapActions, mapState} from "pinia";
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useAuthenticationStore} from '../../state/modules/authentication';
import {useNotificationsStore} from '../../state/modules/notifications';
import User from "../../models/User";
import Loading from "../ui/Loading.vue";
import moment from "moment";
import DatePicker from "../forms/DatePicker.vue";
import SelectDropdown from "../forms/SelectDropdown.vue";
import SimpleInput from '../forms/Input.vue';
import Toggle from "../forms/Toggle.vue";
import Checkbox from "../forms/Checkbox.vue";
import SimpleButton from "../ui/SimpleButton.vue";
import NoCalendarIntegration from "./NoCalendarIntegration.vue";
import Calendar from "../Calendar.vue";
import PopupModalConfirm from "../ui/PopupModalConfirm.vue";
import {ADD_NOTIFICATION} from "../../state/mutation-types";

export default {
    components: {
        PopupModalConfirm,
        Calendar,
        NoCalendarIntegration,
        SimpleButton, Checkbox, Toggle, SelectDropdown, SimpleInput, DatePicker, Loading, CalendarSyncEdit
    },
    props: {
        form: Form,
        subs: Array,
        disabled: Boolean,
        canManage: Boolean,
        hasBulkTimeslots: Boolean,
        creating: Boolean,
    },
    data() {
        return {
            noCronofySubs: true,
            savingTimeslots: false,
            loading: false,
            buffer: {
                before: 0,
                after: 0,
            },
            period: {
                start: moment(),
                end: moment().add(15, 'days'),
            },
            calendarElements: null,
            timeslots: [],
            availablePeriods: [],
            renderCalendar: false,
            showDatesInPastWarning: false,
            cronofyLimit: 34,
        }
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        ...mapState(useAuthenticationStore, ['currentCompanyId', 'currentUserId']),
        user() {
            return useRepo(User)
                .with('calendars')
                .with('calendar_profiles', (q) => {
                    q.with('calendars');
                })
                .find(this.currentUserId);
        },
        noCalendarIntegration() {
            return !this.primaryCalendar || (!this.availabilities || !this.availabilities.length)
        },
        primaryCalendar() {
            return this.user?.primary_calendar_id;
        },
        availabilities() {
            return this.user?.availability_rules;
        },
        hasDatesInThePast() {
            return this.timeslots.filter(slot => this.dateIsInPast(slot)).length;
        },
        futureLimit() {
            return moment().add(this.cronofyLimit, 'days').startOf('day');
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        getLocale() {
            return this.locale === "se" ? "sv" : this.locale;
        },
        saveTimeslots() {
            if (this.hasDatesInThePast) {
                this.showDatesInPastWarning = true;
                return;
            }

            this.$emit('saved');
        },
        getAvailabilities() {
            if (!this.user.cronofy_id) return;

            const subs = [...this.subs.filter(sub => sub.user.cronofy_id && sub.user.availability_rules.length)]
                .map(sub => {
                    return {
                        sub: sub.user.cronofy_id,
                        managed_availability: true,
                        availability_rule_ids: ['work_hours']
                    }
                });

            if (!subs.length) {
                this.noCronofySubs = true;
                this.renderCalendar = false;
                return;
            }

            this.noCronofySubs = false;

            let start = new moment(this.period.start),
                end = new moment(this.period.end)

            if (start.isSame(new Date(), 'day')) {
                start = moment().add(1, 'hour');
            }

            let from = start,
                to = end.set('hours', 23).set('minutes', 59).set('seconds', 59);

            // check if from or to date is an invalid date
            if (!from.isValid() || !to.isValid() || from.isSameOrAfter(to) || to.isSameOrBefore(from)) {
                this[ADD_NOTIFICATION]({
                    type: 'error',
                    message: this.$t('calendar.query_periods.invalid_date')
                })
                return;
            }
            // check if the max range of 34 days is exceeded for either the start or end date
            if (from.diff(moment(), 'days') > this.cronofyLimit || to.diff(moment(), 'days') > this.cronofyLimit) {
                this[ADD_NOTIFICATION]({
                    type: 'error',
                    message: this.$t('calendar.query_periods.max_limit_exceeded')
                })
                return;
            }

            let queryPeriods = [{start: from.format(), end: to.format()}];

            calendar.availabilities({
                availability_query: {
                    participants: [
                        {
                            required: "all",
                            members: subs
                        }
                    ],
                    max_results: 512,
                    required_duration: { minutes: this.form.duration },
                    start_interval: { minutes: 5 },
                    query_periods: queryPeriods,
                },
                locale: this.getLocale(),
            })
                .then(({available_periods}) => this.availablePeriods = available_periods)
                .then(() => this.renderCalendar = true);
        },
        updateDuration() {
            if (this.form.duration < 5) this.form.duration = 5;
        },
        startDate(date) {
            if (!date) return '';
            return moment(date).locale(this.getLocale()).format('LLLL');
        },
        endDate(date) {
            if (!this.form.duration) return '';
            return moment(date).add(this.form.duration, 'minutes').locale(this.getLocale()).format('LT');
        },
        removeTimeslotsInPast() {
            this.timeslots = this.timeslots.filter(slot => !this.dateIsInPast(slot));
            this.form.time_slots = this.timeslots;
            this.$refs.calendar.removeTimeslotsInPast();
        },
        dateIsInPast(slot) {
            return moment(slot.date).isBefore(moment());
        },
        closeWarning() {
            this.showWarning = false;
            this.showDatesInPastWarning = false;
        },
    },
    watch: {
        'period.start'() {
            this.getAvailabilities();
        },
        'period.end'() {
            this.getAvailabilities();
        },
        'form.calendar_open'() {
            this.form.calendar_time_range = this.period;
        },
        'subs'() {
            this.getAvailabilities();
        }
    },
    mounted() {
        this.timeslots = this.form.time_slots;
        this.$nextTick(() => {
            this.getAvailabilities();
        })
    }
}
</script>
